import { useLayoutEffect } from 'react';
import type { RefObject } from 'react';

/** CSS custom property carrying the pinned employer header's height. */
export const PINNED_HEADER_HEIGHT_VAR = '--wp-admin-header-h';

/**
 * Publishes the pinned employer header's rendered height on `<html>` as
 * {@link PINNED_HEADER_HEIGHT_VAR}, kept current with a ResizeObserver.
 *
 * Measured rather than hard-coded because the stack's height is not fixed:
 * the app bar adds the safe-area top inset, and the sub-tab strip only
 * renders on modules that have sub-tabs. Consumers (the sticky pay-run
 * summary card's offset, `scroll-padding-top`) read the variable, and the
 * stylesheet's `:root` default covers the first paint and any environment
 * without ResizeObserver.
 */
export function usePinnedHeaderHeight(
  ref: RefObject<HTMLElement | null>,
  /**
   * Whether the header is currently rendered. App calls this hook before its
   * employee/admin branch, so the ref is empty on the employee surface; this
   * flag re-runs the effect when the admin surface (and the header) mounts.
   */
  active: boolean,
): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const root = document.documentElement;
    const publish = (): void => {
      root.style.setProperty(
        PINNED_HEADER_HEIGHT_VAR,
        `${String(Math.ceil(el.getBoundingClientRect().height))}px`,
      );
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty(PINNED_HEADER_HEIGHT_VAR);
    };
  }, [ref, active]);
}
